import { BrandKit, Slide, SlideType } from "../../types";
import { components } from "@libertify/api-types/media";

// Shared static asset paths
const LOGO_PATH = "/images/Logo_Libertify_Horizontal_fond_blanc_RVB.png";
const PLACEHOLDER_IMAGE_PATH = "/images/theme-image-placeholder.png";
const TITLE_FONT_PATH = "/fonts/GTWalsheim-Rg.ttf";
const SUBTITLES_FONT_PATH = "/fonts/GTWalsheim-CnRg.ttf";
const TEXT_FONT_PATH = "/fonts/GTWalsheim-Rg.ttf";

// Placeholder asset IDs
const LOGO_ASSET_ID = "default-logo";
const PLACEHOLDER_IMAGE_ASSET_ID = "default-placeholder-image";
const TITLE_FONT_ASSET_ID = "default-title-font";
const SUBTITLES_FONT_ASSET_ID = "default-subtitles-font";
const TEXT_FONT_ASSET_ID = "default-text-font";

const PLACEHOLDER_ASSET_PATHS: Record<string, string> = {
  [LOGO_ASSET_ID]: LOGO_PATH,
  [PLACEHOLDER_IMAGE_ASSET_ID]: PLACEHOLDER_IMAGE_PATH,
  [TITLE_FONT_ASSET_ID]: TITLE_FONT_PATH,
  [SUBTITLES_FONT_ASSET_ID]: SUBTITLES_FONT_PATH,
  [TEXT_FONT_ASSET_ID]: TEXT_FONT_PATH,
};

const DEFAULT_BRAND_KIT: BrandKit = {
  id: "default-brandkit",
  name: "Default",
  generated: false,
  assets: [
    {
      asset_id: LOGO_ASSET_ID,
      name: "Logo",
      type: "Picture",
      purpose: "Logo",
      path: LOGO_PATH,
    },
    {
      asset_id: TITLE_FONT_ASSET_ID,
      name: "GT Walsheim Regular",
      type: "Font",
      purpose: "Title",
      path: TITLE_FONT_PATH,
    },
    {
      asset_id: SUBTITLES_FONT_ASSET_ID,
      name: "GT Walsheim Condensed Regular",
      type: "Font",
      purpose: "Subtitles",
      path: SUBTITLES_FONT_PATH,
    },
    {
      asset_id: TEXT_FONT_ASSET_ID,
      name: "GT Walsheim Regular",
      type: "Font",
      purpose: "Text",
      path: TEXT_FONT_PATH,
    },
  ],
  palette: {
    primary_color: "#FAFBFF",
    secondary_color: "#4ECDC4",
    title_color: "#2C3E50",
    subtitle_color: "#4ECDC4",
  },
};

const DEFAULT_SLIDES_PLACEHOLDERS: Slide[] = [
  {
    id: "title",
    slide_type: "title",
    uuid: "title",
    placeholders: {
      title: "title placeholder",
      subtitle: "subtitle placeholder",
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
  {
    id: "image",
    slide_type: "image",
    uuid: "image",
    placeholders: {
      title: "title placeholder",
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
  {
    id: "figure",
    slide_type: "figure",
    uuid: "figure",
    placeholders: {
      title: "title placeholder",
      figure: {
        figure: "20B+",
        unit: "$",
        short_description: "short description placeholder",
      },
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
  {
    id: "paragraph_image",
    slide_type: "paragraph_image",
    uuid: "paragraph_image",
    placeholders: {
      title: "title placeholder",
      text: "Lorem ipsum dolor sit amet consectetur. Sodales dictum pretium imperdiet mauris libero quam quam ultrices. Quam ut elit nisl pharetra vitae.",
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
  {
    id: "bullet_point",
    slide_type: "bullet_point",
    uuid: "bullet_point",
    placeholders: {
      title: "title placeholder",
      bullet_points: [
        "bullet point 1",
        "bullet point 2",
        "bullet point 3",
        "bullet point 4",
        "bullet point 5",
      ],
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
  {
    id: "quote",
    slide_type: "quote",
    uuid: "quote",
    placeholders: {
      quote: "quote placeholder",
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
  {
    id: "double_paragraph",
    slide_type: "double_paragraph",
    uuid: "double_paragraph",
    placeholders: {
      title: "title placeholder",
      paragraph_1: "paragraph 1 placeholder",
      paragraph_2: "paragraph 2 placeholder",
      image_element: {
        asset_id: PLACEHOLDER_IMAGE_ASSET_ID,
        description: "image description placeholder",
        media_type: "image",
      },
    },
  },
];

const MAIN_IMAGE_ITEM: components["schemas"]["SpriteDefinition"] = {
  name: "main_image",
  graphics: {
    type: "AssetImage",
    asset_id: {
      content: {
        input_type: "String",
        type: "Path",
        path: "image",
        additional_path: null,
        index: null,
        attribute: null,
      },
    },
    mask: null,
    keep_ratio: false,
    fit: {
      strategy: "Cover",
      horizontal_align: "center",
      vertical_align: "center",
    },
    border: null,
  },
  attributes: [
    {
      type: "Width",
      rate: 1,
    },
    {
      type: "Height",
      rate: 1,
    },
    {
      type: "Zoom",
      start: {
        type: "Frame",
        frame: 3,
      },
      duration: 150,
      from_rate: 1,
      to_rate: 1.1,
      method: "Linear",
      vertical_align: "bottom",
      horizontal_align: "center",
    },
    {
      type: "LinearMove",
      start: 0,
      duration: 3,
      from_position: {
        type: "Align",
        reference: null,
        horizontal: "outside_left",
        vertical: "top",
        horizontal_ratio: null,
        vertical_ratio: null,
      },
      to_position: {
        type: "Align",
        reference: null,
        horizontal: "center",
        vertical: "top",
        horizontal_ratio: null,
        vertical_ratio: null,
      },
    },
  ],
  horizontal_anchor: "center",
  vertical_anchor: "center",
};

const DEFAULT_SLIDES_ITEMS: Record<
  SlideType,
  components["schemas"]["SpriteDefinition"][]
> = {
  title: [
    {
      name: "background_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "main_image",
      graphics: {
        type: "AssetImage",
        asset_id: {
          content: {
            input_type: "String",
            type: "Path",
            path: "image",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        mask: null,
        keep_ratio: false,
        fit: {
          strategy: "Cover",
          horizontal_align: "center",
          vertical_align: "center",
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
        {
          type: "Zoom",
          start: {
            type: "Frame",
            frame: 3,
          },
          duration: 150,
          from_rate: 1,
          to_rate: 1.1,
          method: "Linear",
          vertical_align: "bottom",
          horizontal_align: "center",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-vpPc4_xgMS-835IbvByb9",
      graphics: {
        type: "Rectangle",
        color: {
          geometry: {
            type: "Linear",
            start: [0.5, 0],
            end: [0.5, 1],
          },
          stops: [
            {
              color: "#00000000",
              position: 0,
            },
            {
              color: "#00000099",
              position: 0.46,
            },
            {
              color: "#000000cc",
              position: 0.89,
            },
          ],
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.23,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.77,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "logo",
      graphics: {
        type: "AssetImage",
        asset_id: "{brand_kit.logo_picture.asset_id}",
        mask: null,
        keep_ratio: true,
        fit: {
          strategy: "Contain",
          horizontal_align: "center",
          vertical_align: "center",
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.353,
            vertical_ratio: 0.071,
          },
        },
        {
          type: "Width",
          rate: 0.298,
        },
        {
          type: "Height",
          rate: 0.045,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "title_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 14,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "title",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "top",
        line_spacing: 0.5,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.107,
            vertical_ratio: 0.696,
          },
        },
        {
          type: "Width",
          rate: 0.804,
        },
        {
          type: "Height",
          rate: 0.176,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 50,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "subtitle_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.subtitle_color}",
        font: "{brand_kit.text_font.asset_id}",
        size: 8,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "subtitle",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "bottom",
        line_spacing: 0.5,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.094,
            vertical_ratio: 0.553,
          },
        },
        {
          type: "Width",
          rate: 0.809,
        },
        {
          type: "Height",
          rate: 0.122,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 50,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
  ],
  image: [
    {
      name: "background_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "main_image",
      graphics: {
        type: "AssetImage",
        asset_id: {
          content: {
            input_type: "String",
            type: "Path",
            path: "image",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        mask: null,
        keep_ratio: false,
        fit: {
          strategy: "Cover",
          horizontal_align: "center",
          vertical_align: "center",
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.672,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
        {
          type: "Zoom",
          start: {
            type: "Frame",
            frame: 3,
          },
          duration: 150,
          from_rate: 1,
          to_rate: 1.1,
          method: "Linear",
          vertical_align: "bottom",
          horizontal_align: "center",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "title_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 14,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "title",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "center",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.071,
            vertical_ratio: 0.723,
          },
        },
        {
          type: "Width",
          rate: 0.87,
        },
        {
          type: "Height",
          rate: 0.162,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 13,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "transition_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-XK0FwpMGa19cu6Hsg6zIN",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.003061224489795918,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.679,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.6790274833337591,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.679,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
  ],
  figure: [
    {
      name: "background_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "secondary_background",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1.003,
        },
        {
          type: "Height",
          rate: 0.65,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-DLoVHrGGlj0nSdb4OBYN-",
      graphics: {
        type: "Rectangle",
        color: {
          geometry: {
            type: "Linear",
            start: [0, 0],
            end: [1, 1],
          },
          stops: [
            {
              color: "#00000000",
              position: 0,
            },
            {
              color: "#0000004d",
              position: 1,
            },
          ],
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.003,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.647,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "figure_0",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.primary_color}",
        font: "{brand_kit.text_font.asset_id}",
        size: 47,
        parameters: {
          content: {
            input_type: "Number",
            type: "Path",
            path: "figure",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: false,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "top",
        line_spacing: 0.05,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.195,
            vertical_ratio: 0.274,
          },
        },
        {
          type: "Width",
          rate: 0.61,
        },
        {
          type: "Height",
          rate: 0.265,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.274,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.195,
            vertical_ratio: 0.274,
          },
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "unit_percent",
      graphics: {
        type: "Switch",
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "unit",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        options: {
          "%": [
            {
              name: "label_text",
              graphics: {
                font: "{brand_kit.text_font.asset_id}",
                size: 20,
                type: "ParameteredText",
                align: "center",
                color: "{brand_kit.palette.primary_color}",
                wrap_text: false,
                background: null,
                parameters: {
                  content: {
                    path: "unit",
                    type: "Path",
                    input_type: "String",
                  },
                },
                upper_case: false,
                adjust_font: true,
                line_spacing: 0.02,
                stroke_color: null,
                vertical_align: "bottom",
              },
              attributes: [
                {
                  type: "Position",
                  position: {
                    type: "Align",
                    vertical: "none",
                    horizontal: "none",
                    vertical_ratio: 0.141,
                    horizontal_ratio: 0.369,
                  },
                },
                {
                  rate: 0.263,
                  type: "Width",
                },
                {
                  rate: 0.142,
                  type: "Height",
                },
                {
                  type: "Move",
                  start: {
                    type: "Frame",
                    frame: 0,
                  },
                  method: "EaseInOut",
                  duration: 10,
                  to_position: {
                    type: "Align",
                    vertical: "none",
                    horizontal: "none",
                    vertical_ratio: 0.141,
                    horizontal_ratio: 0.369,
                  },
                  from_position: {
                    type: "Align",
                    vertical: "none",
                    horizontal: "outside_left",
                    vertical_ratio: 0.141,
                    horizontal_ratio: null,
                  },
                },
              ],
            },
          ],
        },
        default: [
          {
            name: "label_text",
            graphics: {
              font: "{brand_kit.text_font.asset_id}",
              size: 20,
              type: "ParameteredText",
              align: "center",
              color: "{brand_kit.palette.primary_color}",
              wrap_text: false,
              background: null,
              parameters: {
                content: {
                  path: "unit",
                  type: "Path",
                  input_type: "String",
                },
              },
              upper_case: false,
              adjust_font: true,
              line_spacing: 0.02,
              stroke_color: null,
              vertical_align: "bottom",
            },
            attributes: [
              {
                type: "Position",
                position: {
                  type: "Align",
                  vertical: "none",
                  horizontal: "none",
                  vertical_ratio: 0.141,
                  horizontal_ratio: 0.369,
                },
              },
              {
                rate: 0.263,
                type: "Width",
              },
              {
                rate: 0.142,
                type: "Height",
              },
              {
                type: "Move",
                start: {
                  type: "Frame",
                  frame: 0,
                },
                method: "EaseInOut",
                duration: 10,
                to_position: {
                  type: "Align",
                  vertical: "none",
                  horizontal: "none",
                  vertical_ratio: 0.141,
                  horizontal_ratio: 0.369,
                },
                from_position: {
                  type: "Align",
                  vertical: "none",
                  horizontal: "outside_left",
                  vertical_ratio: 0.141,
                  horizontal_ratio: null,
                },
              },
            ],
          },
        ],
      },
      attributes: [],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "title_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 14,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "title",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "left",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "bottom",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.071,
            vertical_ratio: 0.684,
          },
        },
        {
          type: "Width",
          rate: 0.859,
        },
        {
          type: "Height",
          rate: 0.09,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "short_description_text",
      graphics: {
        type: "StreamedText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.text_font.asset_id}",
        size: 8,
        content: {
          content: {
            input_type: "String",
            type: "Path",
            path: "short_description",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: false,
        background: null,
        wrap_text: true,
        align: "left",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "top",
        line_spacing: 0.25,
        words_per_second: 5,
        effects: [
          {
            type: "Appear",
            start: {
              type: "Frame",
              frame: 0,
            },
            duration: 5,
            horizontal: "left",
            horizontal_align: "left",
          },
        ],
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.071,
            vertical_ratio: 0.787,
          },
        },
        {
          type: "Width",
          rate: 0.859,
        },
        {
          type: "Height",
          rate: 0.129,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "transition_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-QY0xw8IOn43feRcI8zIEk",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.primary_color}",
        thickness: 0.005,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -0.003,
            vertical_ratio: 0.657,
          },
        },
        {
          type: "Width",
          rate: 1.003,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    MAIN_IMAGE_ITEM,
  ],
  paragraph_image: [
    {
      name: "background_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "logo",
      graphics: {
        type: "AssetImage",
        asset_id: "{brand_kit.logo_picture.asset_id}",
        mask: null,
        keep_ratio: true,
        fit: {
          strategy: "Contain",
          horizontal_align: "center",
          vertical_align: "center",
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0,
          },
        },
        {
          type: "Height",
          rate: 0.09,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "main_image",
      graphics: {
        type: "AssetImage",
        asset_id: {
          content: {
            input_type: "String",
            type: "Path",
            path: "image",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        mask: null,
        keep_ratio: false,
        fit: {
          strategy: "Cover",
          horizontal_align: "center",
          vertical_align: "center",
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.553,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
        {
          type: "Zoom",
          start: {
            type: "Frame",
            frame: 3,
          },
          duration: 150,
          from_rate: 1,
          to_rate: 1.1,
          method: "Linear",
          vertical_align: "bottom",
          horizontal_align: "center",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "title_text",
      graphics: {
        type: "StreamedText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 14,
        content: {
          content: {
            input_type: "String",
            type: "Path",
            path: "title",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "left",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "bottom",
        line_spacing: 0.25,
        words_per_second: 5,
        effects: [
          {
            type: "Appear",
            start: {
              type: "Frame",
              frame: 0,
            },
            duration: 5,
            horizontal: "left",
            horizontal_align: "left",
          },
        ],
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.065,
            vertical_ratio: 0.618,
          },
        },
        {
          type: "Width",
          rate: 0.871,
        },
        {
          type: "Height",
          rate: 0.095,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 14,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "text",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: false,
        background: null,
        wrap_text: true,
        align: "left",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "top",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.068,
            vertical_ratio: 0.735,
          },
        },
        {
          type: "Width",
          rate: 0.865,
        },
        {
          type: "Height",
          rate: 0.157,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "transition_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-FK7JpF1azXUhWu3e0bAP1",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.primary_color}",
        thickness: 0.003061224489795918,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.561,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.5607142857142857,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.561,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-oMJ7yNNrgUd2ZInhb8tKK",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.0020408163265306124,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.569,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_right",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.5693877551020409,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.569,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
  ],
  bullet_point: [
    {
      name: "background_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-sSBAxf07I-vSkaS4i7LtU",
      graphics: {
        type: "Rectangle",
        color: {
          geometry: {
            type: "Linear",
            start: [0, 0],
            end: [1, 1],
          },
          stops: [
            {
              color: "#0000000a",
              position: 0.26,
            },
            {
              color: "#00000026",
              position: 1,
            },
          ],
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.12,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.18,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.12,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.12,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-Jsl20eR1wWvFIc7xx3bUC",
      graphics: {
        type: "Rectangle",
        color: {
          geometry: {
            type: "Linear",
            start: [1, 0],
            end: [0, 1],
          },
          stops: [
            {
              color: "#0000001a",
              position: 0,
            },
            {
              color: "#00000003",
              position: 0.31,
            },
          ],
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "bullet_points_list",
      graphics: {
        type: "Table",
        item_components: [
          {
            name: "bullet_point_text",
            graphics: {
              font: "{brand_kit.text_font.asset_id}",
              size: 8,
              type: "ParameteredText",
              align: "center",
              color: "{brand_kit.palette.title_color}",
              wrap_text: true,
              background: null,
              parameters: {
                content: {
                  path: "__item__",
                  type: "Path",
                  input_type: "String",
                },
              },
              upper_case: false,
              adjust_font: true,
              line_spacing: 0.25,
              stroke_color: null,
              vertical_align: "center",
            },
            attributes: [
              {
                type: "Position",
                position: {
                  type: "Align",
                  vertical: "none",
                  horizontal: "none",
                  vertical_ratio: 0,
                  horizontal_ratio: 0.05,
                },
              },
              {
                rate: 0.9,
                type: "Width",
              },
              {
                rate: 0.9,
                type: "Height",
              },
              {
                type: "Move",
                start: {
                  type: "Frame",
                  frame: 0,
                },
                method: "EaseInOut",
                duration: 10,
                to_position: {
                  type: "Align",
                  vertical: "none",
                  horizontal: "none",
                  vertical_ratio: 0,
                  horizontal_ratio: 0.05,
                },
                from_position: {
                  type: "Align",
                  vertical: "none",
                  horizontal: "outside_left",
                  vertical_ratio: 0,
                  horizontal_ratio: null,
                },
              },
            ],
          },
        ],
        items_per_row: 1,
        time_interval: 10,
        items: {
          input_type: "StringList",
          type: "Path",
          path: "bullet_points",
          additional_path: null,
          index: null,
          attribute: null,
        },
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.065,
            vertical_ratio: 0.34,
          },
        },
        {
          type: "Width",
          rate: 0.867,
        },
        {
          type: "Height",
          rate: 0.474,
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "title_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 14,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "title",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "center",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.134,
            vertical_ratio: 0.15,
          },
        },
        {
          type: "Width",
          rate: 0.732,
        },
        {
          type: "Height",
          rate: 0.121,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "transition_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-_DL-Vks6BbtAUwoV8OUu9",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.0050062578222778474,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.111,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.111,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.111,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-N1pN2NzFGorJmknDgtPqS",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.0050062578222778474,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.309,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_right",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.309,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.309,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    MAIN_IMAGE_ITEM,
  ],
  double_paragraph: [
    {
      name: "transition_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-0toBD2uGhA6hoj56xxumL",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.001,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.335,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.001,
            vertical_ratio: 0,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-NiI1KjmipI6yQf5x8B6iD",
      graphics: {
        type: "Rectangle",
        color: {
          geometry: {
            type: "Linear",
            start: [0.5, 0],
            end: [0.5, 1],
          },
          stops: [
            {
              color: "#00000000",
              position: 0.01,
            },
            {
              color: "#00000026",
              position: 1,
            },
          ],
        },
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.032,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 0.302,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "title_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.title_font.asset_id}",
        size: 17,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "title",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: true,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "center",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.05,
            vertical_ratio: 0.1,
          },
        },
        {
          type: "Width",
          rate: 0.9,
        },
        {
          type: "Height",
          rate: 0.15,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "paragraph_1_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.text_font.asset_id}",
        size: 8,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "paragraph_1",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: false,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "center",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.056,
            vertical_ratio: 0.38,
          },
        },
        {
          type: "Width",
          rate: 0.888,
        },
        {
          type: "Height",
          rate: 0.241,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.38,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.056,
            vertical_ratio: 0.38,
          },
          method: "EaseInOut",
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 18,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "paragraph_2_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.text_font.asset_id}",
        size: 8,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "paragraph_2",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: false,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "center",
        line_spacing: 0.25,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.064,
            vertical_ratio: 0.645,
          },
        },
        {
          type: "Width",
          rate: 0.88,
        },
        {
          type: "Height",
          rate: 0.243,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 18,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "transition_overlay",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 10,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: -1,
            vertical_ratio: 0,
          },
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-b1BDT8cpbwok4BYq7UuV_",
      graphics: {
        type: "Line",
        orientation: "horizontal",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.003061224489795918,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.341,
          },
        },
        {
          type: "Width",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "outside_left",
            vertical: "none",
            horizontal_ratio: null,
            vertical_ratio: 0.341,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0,
            vertical_ratio: 0.341,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    MAIN_IMAGE_ITEM,
  ],
  quote: [
    {
      name: null,
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.primary_color}",
        border: null,
      },
      attributes: [
        {
          type: "FullScreen",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-rectangle-D2B_-QTDSjK4gCCRQ7XTL",
      graphics: {
        type: "Rectangle",
        color: "{brand_kit.palette.secondary_color}",
        border: null,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.107,
            vertical_ratio: 0,
          },
        },
        {
          type: "Width",
          rate: 0.786,
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 25,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "outside_top",
            horizontal_ratio: 0.107,
            vertical_ratio: null,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.107,
            vertical_ratio: 0,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "quote_text",
      graphics: {
        type: "ParameteredText",
        color: "{brand_kit.palette.title_color}",
        font: "{brand_kit.text_font.asset_id}",
        size: 17,
        parameters: {
          content: {
            input_type: "String",
            type: "Path",
            path: "quote",
            additional_path: null,
            index: null,
            attribute: null,
          },
        },
        upper_case: false,
        background: null,
        wrap_text: true,
        align: "center",
        adjust_font: true,
        stroke_color: null,
        vertical_align: "center",
        line_spacing: 0.05,
        format: "",
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.18,
            vertical_ratio: 0.19,
          },
        },
        {
          type: "Width",
          rate: 0.639,
        },
        {
          type: "Height",
          rate: 0.576,
        },
        {
          type: "Fade",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_opacity: 0,
          to_opacity: 255,
          method: "EaseInOut",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-gcEQX2mnUMQ8czDhdQawJ",
      graphics: {
        type: "Line",
        orientation: "vertical",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.008908685968819599,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.126,
            vertical_ratio: 0,
          },
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "outside_bottom",
            horizontal_ratio: 0.126,
            vertical_ratio: null,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.126,
            vertical_ratio: 0,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-E9dhmaqXc8L_358_pOiQq",
      graphics: {
        type: "Line",
        orientation: "vertical",
        color: "{brand_kit.palette.secondary_color}",
        thickness: 0.008908685968819599,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.874,
            vertical_ratio: 0,
          },
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "outside_bottom",
            horizontal_ratio: 0.874,
            vertical_ratio: null,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.874,
            vertical_ratio: 0,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-Jhw5KUytz99_gAT4Gp5bl",
      graphics: {
        type: "Line",
        orientation: "vertical",
        color: "{brand_kit.palette.primary_color}",
        thickness: 0.005567928730512249,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.91,
            vertical_ratio: 0,
          },
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "outside_top",
            horizontal_ratio: 0.9102249806991944,
            vertical_ratio: null,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.91,
            vertical_ratio: 0,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    {
      name: "slide_shape-line-TUqhUiSrL4kuKRJVQV5zk",
      graphics: {
        type: "Line",
        orientation: "vertical",
        color: "{brand_kit.palette.primary_color}",
        thickness: 0.005567928730512249,
      },
      attributes: [
        {
          type: "Position",
          position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.089,
            vertical_ratio: 0,
          },
        },
        {
          type: "Height",
          rate: 1,
        },
        {
          type: "Move",
          start: {
            type: "Frame",
            frame: 0,
          },
          duration: 38,
          from_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "outside_top",
            horizontal_ratio: 0.089,
            vertical_ratio: null,
          },
          to_position: {
            type: "Align",
            reference: null,
            horizontal: "none",
            vertical: "none",
            horizontal_ratio: 0.089,
            vertical_ratio: 0,
          },
          method: "EaseInOutExpo",
        },
      ],
      horizontal_anchor: "center",
      vertical_anchor: "center",
    },
    MAIN_IMAGE_ITEM,
  ],
};

export default {
  DEFAULT_SLIDES_PLACEHOLDERS,
  DEFAULT_SLIDES_ITEMS,
  DEFAULT_BRAND_KIT,
  PLACEHOLDER_ASSET_PATHS,
  MAIN_IMAGE_ITEM,
};
